import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import React, { Suspense } from 'react';

// Layouts (must load eagerly - they wrap everything)
import MainLayout from '../layouts/MainLayout';
import FrontendLayout from '../layouts/FrontendLayout';
import DashboardLayout from '../layouts/DashboardLayout';
import AuthLayout from '../layouts/AuthLayout';

// Route guards (must load eagerly)
import ProtectedRoute from './ProtectedRoutes/ProtectedRoute';
import ProtectDashboard from './ProtectedRoutes/ProtectDashboard';

// Fallback spinner for lazy-loaded routes
import Preloader from '../pages/dashboard/components/Preloader';

// === Lazy-loaded pages ===

// Main Routes
const Home = React.lazy(() => import('../pages/Home'));
const About = React.lazy(() => import('../pages/About'));
const Contact = React.lazy(() => import('../pages/Contact'));
const Thankyou = React.lazy(() => import('../pages/Thankyou'));
const BookAppointment = React.lazy(() => import('../pages/BookAppointment'));
const Faq = React.lazy(() => import('../pages/Faq'));

// Authentication Routes
const Login = React.lazy(() => import('../pages/auth/Login'));
const VerifyEmail = React.lazy(() => import('../components/forms/VerifyEmail'));
const ForgotPassword = React.lazy(() => import('../pages/auth/ForgotPassword'));
const Existingaccount = React.lazy(() => import('../pages/auth/existing-account'));
const Otp = React.lazy(() => import('../pages/auth/Otp'));
const RegisterTaxPro = React.lazy(() => import('../pages/auth/RegisterTaxPro'));
const LoginWithToken = React.lazy(() => import('../pages/auth/LoginWithToken'));

// Dashboard Routes
const Settings = React.lazy(() => import('../pages/dashboard/Settings'));
const Dashboard = React.lazy(() => import('../pages/dashboard/Dashboard'));
const Notifications = React.lazy(() => import('../pages/dashboard/Notifications'));

// Leads Routes
const LeadList = React.lazy(() => import('../pages/dashboard/leads/list'));
const LeadDetails = React.lazy(() => import('../pages/dashboard/leads/details'));
const CreateContract = React.lazy(() => import('../pages/dashboard/leads/createContract'));
const CreateJob = React.lazy(() => import('../pages/dashboard/leads/CreateJob'));
const CustomerCallBoard = React.lazy(() => import('../pages/dashboard/leads/components/CustomerCallBoard'));

// Jobs Routes
const JobList = React.lazy(() => import('../pages/dashboard/jobs/list'));
const JobDetails = React.lazy(() => import('../pages/dashboard/jobs/details'));
const EditContract = React.lazy(() => import('../pages/dashboard/jobs/editContract'));
const EditJob = React.lazy(() => import('../pages/dashboard/jobs/editJob'));
const JobQuestionnairePage = React.lazy(() => import('../pages/dashboard/jobs/JobQuestionnaire'));
const JobContactPage = React.lazy(() => import('../pages/dashboard/jobs/JobContact'));
const JobDocumentsAndFormsPage = React.lazy(() => import('../pages/dashboard/jobs/JobDocumentsAndForms'));
const JobTaxPlanGeneratorPage = React.lazy(() => import('../pages/dashboard/jobs/JobTaxPlanGenerator'));
const JobNotesPage = React.lazy(() => import('../pages/dashboard/jobs/JobNotesPage'));
const JobInvoicesPage = React.lazy(() => import('../pages/dashboard/jobs/JobInvoices'));

// Projects Routes
const ProjectList = React.lazy(() => import('../pages/dashboard/projects/list'));
const ProjectDetails = React.lazy(() => import('../pages/dashboard/projects/details'));
const Contract = React.lazy(() => import('../pages/dashboard/projects/contract'));
const ProjectDocumentsAndFormsPage = React.lazy(() => import('../pages/dashboard/projects/ProjectDocumentsAndFormsPage'));
const ProjectInvoicesPage = React.lazy(() => import('../pages/dashboard/projects/ProjectInvoices'));
const ProjectQuestionnairePage = React.lazy(() => import('../pages/dashboard/projects/ProjectQuestionnaire'));

// Invoices & Payouts
const InvoiceList = React.lazy(() => import('../pages/dashboard/invoices/list'));
const InvoiceDetail = React.lazy(() => import('../pages/dashboard/invoices/detail'));
const PayoutList = React.lazy(() => import('../pages/dashboard/payouts/list'));
const DisputesList = React.lazy(() => import('../pages/dashboard/disputes/list'));

// Other Dashboard Pages
const AppointmentList = React.lazy(() => import('../pages/dashboard/appointments/list'));
const DocumentList = React.lazy(() => import('../pages/dashboard/documents/list'));
const QRCodePage = React.lazy(() => import('../pages/dashboard/documents/QRCodePage'));
const DocumentUploadMonile = React.lazy(() => import('../pages/dashboard/documents/DocumentUploadMonile'));
const UserList = React.lazy(() => import('../pages/dashboard/users/list'));
const userDetails = React.lazy(() => import('../pages/dashboard/users/details'));
const MessageList = React.lazy(() => import('../pages/dashboard/messages/list'));
const Organizer = React.lazy(() => import('../pages/dashboard/organizer/organizer'));
const Questionnaire = React.lazy(() => import('../pages/dashboard/Questionnaire'));
const FormList = React.lazy(() => import('../pages/dashboard/forms/list'));
const DocumentsGroup = React.lazy(() => import('../pages/dashboard/documentsgroup/list'));
const TokenList = React.lazy(() => import('../pages/dashboard/tokens/list'));
const TransactionsList = React.lazy(() => import('../pages/dashboard/transactions/TransactionsList'));
const BadgesList = React.lazy(() => import('../pages/dashboard/badges/BadgesList'));
const ProductsList = React.lazy(() => import('../pages/dashboard/Products/ProductsList'));
const ErrorLogsList = React.lazy(() => import('../pages/dashboard/Logs/ErrorLogsList'));
const VideosList = React.lazy(() => import('../pages/dashboard/videos/VideoList'));
const VideoLibrary = React.lazy(() => import('../pages/dashboard/videos/VideoLibrary'));
const Courses = React.lazy(() => import('../pages/dashboard/courses/Courses'));
const Course = React.lazy(() => import('../pages/dashboard/courses/Course'));
const ViolationsList = React.lazy(() => import('../pages/dashboard/violations/ViolationsList'));
const CouponList = React.lazy(() => import('../pages/dashboard/coupons/CouponList'));
const Resources = React.lazy(() => import('../pages/dashboard/resources/Resources'));
const Merna = React.lazy(() => import('../pages/dashboard/resources/Merna'));
const CaseStudies = React.lazy(() => import('../pages/dashboard/resources/CaseStudies'));
const CoachingCalls = React.lazy(() => import('../pages/dashboard/CoachingCalls'));
const RevenueDashboard = React.lazy(() => import('../pages/dashboard/revenue/RevenueDashboard'));
const Recordings = React.lazy(() => import('../pages/dashboard/recording/list'));
const ActiveUpcomingTasks = React.lazy(() => import('../pages/dashboard/tasks/list'));

// Billing
const Billing = React.lazy(() => import('../pages/dashboard/billing/list'));
const Membership = React.lazy(() => import('../pages/dashboard/billing/membership'));
const AdminBilling = React.lazy(() => import('../pages/dashboard/billing/adminBilling'));
const SuccessThanks = React.lazy(() => import('../pages/dashboard/billing/successThanks'));
const AddPaymentMethods = React.lazy(() => import('../pages/dashboard/billing/addPaymentMethod'));

// Admin
const KeapVerification = React.lazy(() => import('../pages/dashboard/Admin/keapVerification'));
const CalendlyVerification = React.lazy(() => import('../pages/dashboard/CalendlyVerification'));
const OutlookCalendarVerification = React.lazy(() => import('../pages/dashboard/OutlookCalendarVerification'));
const GoogleCalendarVerification = React.lazy(() => import('../pages/dashboard/GoogleCalendarVerification'));
const GhlVerification = React.lazy(() => import('../pages/dashboard/GhlVerification'));
const InternalCheckout = React.lazy(() => import('../pages/dashboard/internal-checkout/list'));

// Public / Standalone Pages
const Profile = React.lazy(() => import("../pages/Profile"));
const UserAgreement = React.lazy(() => import('../pages/UserAgreement'));
const PrivacyPolicy = React.lazy(() => import('../pages/PrivacyPolicy'));
const SignatureSumbitted = React.lazy(() => import('../pages/SignatureSumbitted'));
const ContractSubmitted = React.lazy(() => import('../pages/ContractSubmitted'));
const ContractSubmittedTimer = React.lazy(() => import('../pages/ContractSubmittedTimer'));
const AppointmentDetails = React.lazy(() => import('../pages/AppointmentDetails'));
const AppointmentStatus = React.lazy(() => import('../pages/AppointmentStatus'));
const MultyStepForm = React.lazy(() => import('../components/forms/MultyStepLeadGeneration/MultyStepForm'));
const PublicCheckout = React.lazy(() => import('../pages/checkout/Checkout'));
const CheckoutSuccess = React.lazy(() => import('../pages/checkout/CheckoutSuccess'));
const CheckoutPending = React.lazy(() => import('../pages/checkout/CheckoutPending'));

const NotFound = React.lazy(() => import('../pages/NotFound'));


const RoutesCollection = () => {
    return (
        <Router>
            <Suspense fallback={<Preloader />}>
            <Routes>
                <Route path="/" element={<MainLayout />}>
                <Route index element={<Navigate to="/login" replace />} />
                <Route path="checkout/success" element={<CheckoutSuccess />} />
                <Route path="checkout/pending" element={<CheckoutPending />} />
				<Route path="checkout/:uuid" element={<PublicCheckout />} />
                <Route path="signatureSumbitted" element={<SignatureSumbitted />} />
                <Route path="contractSubmitted" element={<ContractSubmitted />} />
                <Route path="signatureSumbitted/:id" element={<ContractSubmittedTimer path="/dashboard/projects" showTimer={true} />} />
                    {/* <Route path="/500" element={<Error500 />} /> */}
                    <Route path="*" element={<FrontendLayout />}>
                        <Route index element={<Home />} />
                        <Route path="about" element={<About />} />
                        <Route path="contact" element={<Contact />} />
                        <Route path="thankyou" element={<Thankyou />} />
                        <Route path="book-appointment" element={<BookAppointment />} />
                        <Route path="faq" element={<Faq />} />
                        <Route path="user-agreement" element={<UserAgreement />} />
                        <Route path="signatureSumbitted" element={<SignatureSumbitted />} />
                        <Route path="privacy-policy" element={<PrivacyPolicy />} />
                        <Route path="profile/:username/:uuid" element={<ProtectedRoute login={true} Component={Profile} path="/profile" />} />
                        <Route path='login-with-token/:token' element={<LoginWithToken /> } />

                    </Route>

                    <Route
                        path="*"
                        element={<ProtectedRoute login={false} Component={AuthLayout} path="/dashboard/" />}
                    >
                        <Route path="login" element={<Login />} />
                        <Route path="forgot-password" element={<ForgotPassword />} />
                        <Route path="existing-account" element={<Existingaccount />} />
                        <Route path="api/user/:status/:id" element={<VerifyEmail />} />
                        <Route path="walkthrough" element={<MultyStepForm />} />
                        <Route
                            path="registerTaxPro"
                            element={
                                <ProtectedRoute login={false} Component={RegisterTaxPro} path="/dashboard/" />
                            }
                        />
                        <Route path="appointment-details/:id" element={<AppointmentDetails />} />

                        <Route path="qr-code/:uuid" element={<QRCodePage />} />
                        <Route path="qr-upload/:uuid" element={<DocumentUploadMonile />} />
                    </Route>
                    <Route
                        path="/otp"
                        element={<ProtectedRoute login={true} Component={Otp} path="/login" />}
                    />
                    <Route
                        path="*"
                        element={<ProtectedRoute login={true} Component={DashboardLayout} path="/login" />}
                    >
                        <Route path="dashboard" element={<ProtectDashboard Component={Dashboard} />} />
                        <Route path="dashboard/signatureSumbitted" element={<SignatureSumbitted />} />
                        <Route path="dashboard/leads" element={<ProtectDashboard Component={LeadList} />} />
                        <Route path="dashboard/leads/:uuid" element={<ProtectDashboard Component={LeadDetails} />} />
                        <Route path="dashboard/createContract/:uuid" element={<ProtectDashboard Component={CreateContract} />} />
                        <Route path="dashboard/jobs/create/:uuid" element={<ProtectDashboard Component={CreateJob} />} />
                        <Route path="dashboard/editContract/:uuid" element={<ProtectDashboard Component={EditContract} />} />
                        <Route path="dashboard/jobs/edit/:uuid" element={<ProtectDashboard Component={EditJob} />} />
                        <Route path="dashboard/jobs" element={<ProtectDashboard Component={JobList} />} />
                        {/* / JOB DETAIL PAGES / */}
                        <Route path="dashboard/jobs/:uuid" element={<ProtectDashboard Component={JobDetails} />} />
                        <Route path="dashboard/job/contact/:uuid" element={<ProtectDashboard Component={JobContactPage} />} />
                        <Route path="dashboard/job/questionnaires/:uuid" element={<ProtectDashboard Component={JobQuestionnairePage} />} />
                        <Route path="dashboard/job/documents-forms/:uuid" element={<ProtectDashboard Component={JobDocumentsAndFormsPage} />} />
                        <Route path="dashboard/job/tax-plan-generator/:uuid" element={<ProtectDashboard Component={JobTaxPlanGeneratorPage} />} />
                        <Route path="dashboard/job/invoices/:uuid" element={<ProtectDashboard Component={JobInvoicesPage} />} />
                        <Route path="dashboard/job/notes/:uuid" element={<ProtectDashboard Component={JobNotesPage} />} />
                        {/* / / */}
                        <Route path="dashboard/form-list" element={<ProtectDashboard Component={FormList} />} />
                        <Route path="dashboard/calls" element={<ProtectDashboard Component={CustomerCallBoard} />} />
                        <Route path="dashboard/calls/:uuid/details" element={<ProtectDashboard Component={CustomerCallBoard} />} />
                        <Route path="dashboard/messages" element={<ProtectDashboard Component={MessageList} />} />
                        <Route path="dashboard/messages/:uuid/details" element={<ProtectDashboard Component={MessageList} />} />
                        <Route path="dashboard/invoices" element={<ProtectDashboard Component={InvoiceList} />} />
                        <Route path="dashboard/invoices/detail" element={<ProtectDashboard Component={InvoiceDetail} />} />
                        <Route path="dashboard/invoices/payouts" element={<ProtectDashboard Component={PayoutList} />} />
                        <Route path="dashboard/invoices/disputes" element={<ProtectDashboard Component={DisputesList} />} />
                        <Route path="dashboard/invoices/:id" element={<ProtectDashboard Component={InvoiceDetail} />} />
                        <Route path="dashboard/projects" element={<ProtectDashboard Component={ProjectList} />} />
                        {/* / PROJECT DETAIL PAGES/ */}
                        <Route path="dashboard/projects/:uuid" element={<ProtectDashboard Component={ProjectDetails} />} />
                        <Route path="dashboard/project/documents-forms/:uuid" element={<ProtectDashboard Component={ProjectDocumentsAndFormsPage} />} />
                        <Route path="dashboard/project/invoices/:uuid" element={<ProtectDashboard Component={ProjectInvoicesPage} />} />
                        <Route path="dashboard/project/questionnaires/:uuid" element={<ProtectDashboard Component={ProjectQuestionnairePage} />} />
                        {/* / / */}
                        <Route path="dashboard/contract/:id" element={<ProtectDashboard Component={Contract} />} />
                        <Route path="dashboard/appointments" element={<ProtectDashboard Component={AppointmentList} />} />
                        <Route path="dashboard/appointments/change-status/:statusId/:uuid" element={<ProtectDashboard Component={AppointmentStatus} />} />
                        <Route path="dashboard/billing" element={<ProtectDashboard Component={Billing} />} />
                        <Route path="dashboard/adminbilling" element={<ProtectDashboard Component={AdminBilling} />} />
                        <Route path="dashboard/membership" element={<ProtectDashboard Component={Membership} />} />
                        <Route path="dashboard/checkout" element={<ProtectDashboard Component={AddPaymentMethods} />} />
                        <Route path="dashboard/confirmed" element={<ProtectDashboard Component={SuccessThanks} />} />
                        <Route path="dashboard/tasks" element={<ProtectDashboard Component={ActiveUpcomingTasks} />} />
                        <Route path="dashboard/recordings" element={<ProtectDashboard Component={Recordings} />} />
                        <Route path="dashboard/documents" element={<ProtectDashboard Component={DocumentList} />} />
                        <Route path="dashboard/settings" element={<ProtectDashboard Component={Settings} />} />
                        <Route path="dashboard/notifications" element={<ProtectDashboard Component={Notifications} />} />
                        <Route path="dashboard/onboard" element={<ProtectDashboard />} />
                        <Route path="dashboard/users" element={<ProtectDashboard Component={UserList} />} />
                        <Route path="dashboard/users/:uuid" element={<ProtectDashboard Component={userDetails} />} />
                        <Route path="dashboard/underReview" element={<ProtectDashboard />} />
                        <Route path="dashboard/organizer" element={<ProtectDashboard Component={Organizer} />} />
                        <Route path="dashboard/questionnaire" element={<ProtectDashboard Component={Questionnaire} />} />
                        <Route path="dashboard/verify" element={<ProtectDashboard Component={KeapVerification} />} />
                        <Route path="dashboard/calendly/auth" element={<ProtectDashboard Component={CalendlyVerification} />} />
                        <Route path="dashboard/outlook_calendar/auth" element={<ProtectDashboard Component={OutlookCalendarVerification} />} />
                        <Route path="dashboard/google_calendar/auth" element={<ProtectDashboard Component={GoogleCalendarVerification} />} />
                        <Route path="dashboard/ghl/auth" element={<ProtectDashboard Component={GhlVerification} />} />
                        <Route path='dashboard/documentgroup-list' element={<ProtectDashboard Component={DocumentsGroup} />} />
                        <Route path='dashboard/tokens' element={<ProtectDashboard Component={TokenList} />} />
                        <Route path='dashboard/transactions' element={<ProtectDashboard Component={TransactionsList} />} />
                        <Route path='dashboard/badges' element={<ProtectDashboard Component={BadgesList} />} />
                        <Route path='dashboard/products' element={<ProtectDashboard Component={ProductsList} />} />
                        <Route path='dashboard/error-logs' element={<ProtectDashboard Component={ErrorLogsList} />} />
                        <Route path='dashboard/videos' element={<ProtectDashboard Component={VideosList} />} />
                        <Route path='dashboard/videos/:uuid' element={<ProtectDashboard Component={VideoLibrary} />} />
                        <Route path='dashboard/courses' element={<ProtectDashboard Component={Courses} />} />
                        <Route path='dashboard/courses/:uuid' element={<ProtectDashboard Component={Course} />} />
                        <Route path='dashboard/violations' element={<ProtectDashboard Component={ViolationsList} />} />
                        <Route path='dashboard/coupons' element={<ProtectDashboard Component={CouponList} />} />
                        <Route path='dashboard/resources' element={<ProtectDashboard Component={Resources} />} />
                        <Route path='dashboard/resources/merna' element={<ProtectDashboard Component={Merna} />} />
                        <Route path='dashboard/resources/merna/casestudy/:id' element={<ProtectDashboard Component={CaseStudies} />} />
                        <Route path='dashboard/coaching-calls' element={<ProtectDashboard Component={CoachingCalls} />} />
                        <Route path='dashboard/revenue' element={<ProtectDashboard Component={RevenueDashboard} />} />
                        <Route path='dashboard/internal-checkout' element={<ProtectDashboard Component={InternalCheckout} />} />
                        <Route path="*" element={<NotFound />} />
                    </Route>
                </Route>
            </Routes>
            </Suspense>
        </Router>
    );
};

export default RoutesCollection;
